import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Flex, LegacyText, themedColor } from '@lemonade-hq/cdk';
import { TextReview } from '../types';
import { VERTICAL_MEDIA_QUERIES } from './verticalMediaQueries';
import { Controls } from './Controls';

const ContentWrapper = styled(Flex)`
  width: 100%;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
  background-color: ${themedColor('cloud')};
  flex-direction: column;
  justify-content: space-between;
  padding: 60px 46px 100px;
  position: relative;

  ${VERTICAL_MEDIA_QUERIES.med`
        padding: 50px 40px 80px;
    `}

  ${VERTICAL_MEDIA_QUERIES.sm`
        padding: 54px 30px 36px;
    `}
`;

const MainText = styled(LegacyText)`
  font-size: 2.8rem;
  padding-inline: 0;
  line-height: 1.6;

  ${VERTICAL_MEDIA_QUERIES.medBig`
        font-size: 2rem;
    `}

  ${VERTICAL_MEDIA_QUERIES.med`
        font-size: 1.8rem;
    `}

    ${VERTICAL_MEDIA_QUERIES.medSm`
        font-size: 1.6rem;
    `}
`;

const TOTAL_LENGTH = 10000;
const PROGRESS_INTERVAL = 250;

interface TextPlayerContentProps {
  review: TextReview;
  isActive: boolean;
  onEnd: () => void;
}

export const TextPlayerContent: React.FC<TextPlayerContentProps> = ({
  review: { fullText, reviewerName, source },
  isActive,
  onEnd,
}) => {
  const [isPaused, setIsPaused] = useState(true);
  const [progress, setProgress] = useState(0);

  const togglePlay = useCallback(() => {
    setIsPaused(prev => !prev);
  }, []);

  const seekProgress = useCallback((progress: number) => {
    setProgress(progress * TOTAL_LENGTH);
  }, []);

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (isActive) {
      setIsPaused(false);
    } else {
      setIsPaused(true);
      timeout = setTimeout(() => {
        setProgress(0);
      }, 1000);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [isActive]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (!isPaused) {
      if (progress >= TOTAL_LENGTH) {
        onEnd();
        return;
      }
      timeout = setTimeout(() => {
        setProgress(progress + PROGRESS_INTERVAL);
      }, PROGRESS_INTERVAL);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [isActive, isPaused, onEnd, progress]);

  return (
    <ContentWrapper>
      <MainText type="main" fontWeight={600}>
        {fullText}
      </MainText>
      <Controls
        progress={(progress / TOTAL_LENGTH) * 100}
        togglePlay={togglePlay}
        isPaused={isPaused}
        reviewerName={reviewerName}
        source={source}
        seekProgress={seekProgress}
      />
    </ContentWrapper>
  );
};
